<script setup>
import axios from '@/utils/http.js'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
    webName: {
        type: String,
        default: ''
    },
    title: {
        type: String,
        default: '新闻动态'
    }
})

const tabs = computed(() => [
    { id: 1, name: t('index.new4') || '新闻中心' }
])

const API_CONFIG = Object.freeze({
    1: { endpoint: '/news/list', limit: 8 }
})

const CACHE_EXPIRY = 5 * 60 * 1000

const activeTabId = ref(1)
const displayedTabId = ref(1)
const newsLists = ref([])
const isLoading = ref(false)
const requestSerial = ref(0)

const newsCache = ref({
    1: { data: [], timestamp: 0 }
})

const featuredNews = computed(() => newsLists.value[0] || null)
const cardNews = computed(() => newsLists.value.slice(1, 8))
const isInitialLoading = computed(() => isLoading.value && !featuredNews.value)
const isSwitching = computed(() => isLoading.value && Boolean(featuredNews.value))
const contentRenderKey = computed(() => `${displayedTabId.value}-${featuredNews.value?.id || 'empty'}`)


const viewAllLink = computed(() => '/new_Smell.html')

const parseDate = (timestamp) => {
    if (!timestamp) return null

    let date

    if (typeof timestamp === 'string') {
        const numeric = Number(timestamp)
        date = Number.isNaN(numeric)
            ? new Date(timestamp.replace(/-/g, '/'))
            : new Date(numeric < 10000000000 ? numeric * 1000 : numeric)
    } else {
        date = new Date(timestamp < 10000000000 ? timestamp * 1000 : timestamp)
    }

    return Number.isNaN(date.getTime()) ? null : date
}

const formatDate = (timestamp) => {
    const date = parseDate(timestamp)
    if (!date) return '--'

    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    return `${year}-${month}-${day}`
}

const formatFeaturedDate = (timestamp) => {
    const date = parseDate(timestamp)

    if (!date) {
        return {
            month: '--',
            day: '--',
            year: '----',
            weekday: '--'
        }
    }

    const weekdays = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六']

    return {
        month: String(date.getMonth() + 1).padStart(2, '0'),
        day: String(date.getDate()).padStart(2, '0'),
        year: String(date.getFullYear()),
        weekday: weekdays[date.getDay()]
    }
}

const featuredDate = computed(() => formatFeaturedDate(featuredNews.value?.create_time))

const stripHtml = (value = '') => {
    return String(value)
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, ' ')
        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, ' ')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;|&ensp;|&emsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/\s+/g, ' ')
        .trim()
}

const getSummary = (item, maxLength = 92) => {
    if (!item) return ''

    const raw =
        item.description ||
        ''

    const text = stripHtml(raw)
    return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text
}

const fetchNews = async (id, force = false) => {
    const config = API_CONFIG[id]
    if (!config) return

    const cache = newsCache.value[id]
    const now = Date.now()

    if (!force && cache.data.length && now - cache.timestamp < CACHE_EXPIRY) {
        newsLists.value = cache.data
        displayedTabId.value = id
        return
    }

    const currentRequest = ++requestSerial.value
    isLoading.value = true

    try {
        const response = await axios.get(config.endpoint, {
            params: {
                page: 1,
                limit: config.limit
            },
            timeout: 8000
        })

        if (currentRequest !== requestSerial.value) return

        const list = Array.isArray(response.data?.data?.list)
            ? response.data.data.list
            : []
        const displayedList = list.slice(0, config.limit)

        cache.data = displayedList
        cache.timestamp = Date.now()
        newsLists.value = displayedList
        displayedTabId.value = id
    } catch (error) {
        if (currentRequest !== requestSerial.value) return

        console.error('新闻数据获取失败：', error)

        if (cache.data.length) {
            newsLists.value = cache.data
            displayedTabId.value = id
        } else if (newsLists.value.length) {
            activeTabId.value = displayedTabId.value
        }
    } finally {
        if (currentRequest === requestSerial.value) {
            isLoading.value = false
        }
    }
}

const handleTabChange = (id) => {
    if (activeTabId.value === id || isLoading.value) return

    activeTabId.value = id
    fetchNews(id)
}

const navigateToDetail = (id) => {
    if (!id || isSwitching.value) return
    window.open(`/press/${id}.htm`, '_blank')
}

onMounted(() => {
    fetchNews(activeTabId.value)
})
</script>

<template>
    <section class="news-showcase">
        <div class="news-showcase__shell Heziyun-defaultWidth">
            <!-- 背景装饰全部使用 SVG，不依赖任何图片文件 -->
            <svg class="news-showcase__background" viewBox="0 0 1600 900" fill="none" aria-hidden="true">
                <defs>
                    <linearGradient id="backgroundCloud" x1="0" y1="0" x2="1" y2="1">
                        <stop stop-color="#FFFFFF" stop-opacity=".76" />
                        <stop offset="1" stop-color="#FFFFFF" stop-opacity=".08" />
                    </linearGradient>
                    <radialGradient id="backgroundGlow">
                        <stop stop-color="#FFFFFF" stop-opacity=".6" />
                        <stop offset="1" stop-color="#FFFFFF" stop-opacity="0" />
                    </radialGradient>
                    <filter id="cloudBlur" x="-40%" y="-40%" width="180%" height="180%">
                        <feGaussianBlur stdDeviation="8" />
                    </filter>
                </defs>

                <g opacity=".76" filter="url(#cloudBlur)">
                    <path d="M-26 348c0-25 20-45 45-45 6 0 12 1 18 4 8-23 30-39 56-39 31 0 57 22 62 51 4-1 8-2 13-2 25 0 45 20 45 45s-20 45-45 45H18c-24 0-44-19-44-43 0-6 1-11 3-16Z" fill="url(#backgroundCloud)" />
                    <path d="M1435 352c0-23 19-42 42-42 6 0 12 1 17 4 8-22 29-37 54-37 29 0 53 20 59 47 4-1 8-2 12-2 23 0 42 19 42 42s-19 42-42 42h-142c-23 0-42-18-42-41 0-5 1-10 3-15Z" fill="url(#backgroundCloud)" />
                </g>

                <circle cx="128" cy="122" r="88" fill="url(#backgroundGlow)" opacity=".12" />
                <circle cx="1480" cy="120" r="120" fill="url(#backgroundGlow)" opacity=".1" />
                <circle cx="1324" cy="270" r="4" fill="#fff" opacity=".42" />
                <circle cx="1390" cy="226" r="2.5" fill="#fff" opacity=".52" />
                <circle cx="211" cy="242" r="3" fill="#fff" opacity=".36" />
            </svg>

            <header class="news-showcase__header">
                <div class="news-showcase__heading">
                    <span class="news-showcase__eyebrow">NEWS</span>
                    <h2>{{ title }}</h2>
                </div>

                <div class="news-showcase__tabs" role="tablist" aria-label="新闻分类">
                    <button
                        v-for="item in tabs"
                        :key="item.id"
                        type="button"
                        class="news-showcase__tab"
                        :class="{ 'is-active': activeTabId === item.id }"
                        :aria-selected="activeTabId === item.id"
                        :disabled="isLoading"
                        @click="handleTabChange(item.id)"
                    >
                        {{ item.name }}
                    </button>
                </div>
            </header>

            <div
                class="news-showcase__body"
                :class="{ 'is-switching': isSwitching }"
                :aria-busy="isLoading"
            >
                <Transition name="news-fade" mode="out-in">
                    <div v-if="isInitialLoading" key="loading" class="news-loading" aria-label="正在加载">
                        <div class="news-loading__featured"></div>
                        <div class="news-loading__grid">
                            <div v-for="index in 7" :key="index" class="news-loading__item"></div>
                        </div>
                    </div>

                    <div v-else-if="featuredNews" :key="contentRenderKey" class="news-showcase__content">
                        <article class="featured-card" @click="navigateToDetail(featuredNews.id)">
                            <div class="featured-card__copy">
                                <div class="featured-card__title-row">
                                    <span class="featured-card__badge">置顶</span>
                                    <h3>{{ featuredNews.title }}</h3>
                                </div>

                                <p v-if="getSummary(featuredNews)">{{ getSummary(featuredNews, 80) }}</p>

                                <button
                                    type="button"
                                    class="featured-card__detail"
                                    @click.stop="navigateToDetail(featuredNews.id)"
                                >
                                    查看详情
                                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                        <path d="M5 12h13M14 7l5 5-5 5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
                                    </svg>
                                </button>
                            </div>

                            <!-- 主视觉：扩i音器、晶体、云朵、轨道均为内联 SVG -->
                            <img src="/src/static/img/new_Smell/86b53475-2e87-42a5-aabb-79ca80111b71.png" class="featured-card__art" alt="">

                            <div class="featured-card__date" aria-label="发布日期">
                                <div class="featured-card__date-main">
                                    <span>{{ featuredDate.month }}</span>
                                    <i>/</i>
                                    <span>{{ featuredDate.day }}</span>
                                </div>
                                <div class="featured-card__date-side">
                                    <span>{{ featuredDate.year }}</span>
                                    <span>{{ featuredDate.weekday }}</span>
                                </div>
                            </div>
                        </article>

                        <div class="news-list">
                            <article
                                v-for="item in cardNews"
                                :key="item.id"
                                class="news-item"
                                tabindex="0"
                                @click="navigateToDetail(item.id)"
                                @keydown.enter="navigateToDetail(item.id)"
                            >
                                <div class="news-item__icon" aria-hidden="true">
                                    <svg viewBox="0 0 32 32" fill="none">
                                        <path d="M6.5 13.1v5.8a2.6 2.6 0 0 0 2.6 2.6h2l1.7 5.1h3.5l-1.6-5.1h1.8l9.2 4.4V6.1l-9.2 4.4H9.1a2.6 2.6 0 0 0-2.6 2.6Z" stroke="currentColor" stroke-width="2.2" stroke-linejoin="round" />
                                        <path d="M27.7 11.7c1 1 1.6 2.4 1.6 4.3 0 1.9-.6 3.3-1.6 4.3" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" />
                                    </svg>
                                </div>

                                <div class="news-item__content">
                                    <h3>{{ item.title }}</h3>
                                    <time>{{ formatDate(item.create_time) }}</time>
                                </div>

                                <span class="news-item__arrow" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" fill="none">
                                        <path d="M5 12h13M14 7l5 5-5 5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
                                    </svg>
                                </span>
                            </article>
                        </div>

                        <a class="news-showcase__more" :href="viewAllLink">
                            查看更多新闻
                            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <path d="M5 12h13M14 7l5 5-5 5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </a>
                    </div>

                    <div v-else key="empty" class="news-empty">
                        <svg viewBox="0 0 64 64" fill="none" aria-hidden="true">
                            <path d="M18 12h22l10 10v30H18V12Z" fill="#F5F8FF" stroke="#9CB9EC" stroke-width="2" />
                            <path d="M40 12v11h10" stroke="#9CB9EC" stroke-width="2" />
                            <path d="M25 31h18M25 38h14" stroke="#6D98E7" stroke-width="2" stroke-linecap="round" />
                        </svg>
                        <span>暂无相关内容</span>
                    </div>
                </Transition>

                <Transition name="switch-fade">
                    <div v-if="isSwitching" class="news-showcase__switching" aria-label="正在切换内容">
                        <span></span>
                    </div>
                </Transition>
            </div>
        </div>
    </section>
</template>

<style scoped>
.news-showcase {
    --news-blue: #0b62f4;
    --news-dark: #101827;
    --news-muted: #7485a5;
    --news-border: rgba(190, 211, 244, 0.72);
    --news-ease: cubic-bezier(0.22, 1, 0.36, 1);
    position: relative;
    padding: 20px 0 32px;
    color: var(--news-dark);
    position: relative;
    background: radial-gradient(circle at 49% 8%, rgba(94, 164, 255, 0.45), transparent 31%), linear-gradient(180deg, #075be9 0%, #2677f4 27%, #6da6f7 43%, #eef5ff 55%, #ffffff 69%, #ffffff 100%);
}

.news-showcase *,
.news-showcase *::before,
.news-showcase *::after {
    box-sizing: border-box;
}

.news-showcase__shell {
    margin: 0 auto;
    overflow: hidden;
    isolation: isolate;
}

.news-showcase__background {
    position: absolute;
    z-index: 0;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
}

.news-showcase__header {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 40px;
    min-height: 192px;
    padding: 33px 70px 23px;
}

.news-showcase__heading {
    min-width: 0;
    color: #fff;
}

.news-showcase__eyebrow {
    display: block;
    margin-bottom: 7px;
    font-size: 24px;
    font-weight: 400;
    line-height: 1;
    letter-spacing: 0.3px;
    opacity: 0.96;
}

.news-showcase__heading h2 {
    margin: 0;
    font-size: clamp(38px, 3.1vw, 48px);
    font-weight: 700;
    line-height: 1.14;
    letter-spacing: -1.8px;
    text-shadow: 0 5px 16px rgba(0, 58, 160, 0.18);
}

.news-showcase__heading p {
    margin: 13px 0 0;
    color: rgba(255, 255, 255, 0.93);
    font-size: 17px;
    line-height: 1.7;
}

.news-showcase__tabs {
    display: grid;
    grid-template-columns: 1fr;
    width: min(196px, 34vw);
    min-width: 160px;
    height: 60px;
    padding: 2px;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.75);
    border-radius: 999px;
    background: rgba(248, 251, 255, 0.95);
    box-shadow:
        0 14px 30px rgba(0, 70, 180, 0.17),
        inset 0 1px 0 #fff;
    backdrop-filter: blur(12px);
}

.news-showcase__tab {
    position: relative;
    border: 0;
    border-radius: 999px;
    color: #50617f;
    font: inherit;
    font-size: 18px;
    cursor: pointer;
    background: transparent;
    transition: color 0.25s ease, background 0.25s ease, box-shadow 0.25s ease;
}

.news-showcase__tab::after {
    position: absolute;
    bottom: 8px;
    left: 50%;
    width: 42px;
    height: 3px;
    border-radius: 999px;
    content: "";
    opacity: 0;
    background: var(--news-blue);
    transform: translateX(-50%) scaleX(0.4);
    transition: opacity 0.25s ease, transform 0.28s var(--news-ease);
}

.news-showcase__tab:hover:not(:disabled) {
    color: var(--news-blue);
}

.news-showcase__tab:disabled {
    cursor: progress;
}

.news-showcase__tab.is-active {
    color: #075ce6;
    font-weight: 600;
    background: #fff;
    box-shadow: 7px 0 18px rgba(38, 88, 166, 0.08);
}

.news-showcase__tab.is-active::after {
    opacity: 1;
    transform: translateX(-50%) scaleX(1);
}

.news-showcase__body {
    position: relative;
    z-index: 3;
    min-height: 666px;
    margin: 0 70px 43px;
}

.news-showcase__content {
    position: relative;
}

.featured-card {
    position: relative;
    min-height: 230px;
    border: 1px solid rgba(208, 220, 240, 0.92);
    border-radius: 17px;
    cursor: pointer;
    background:
        radial-gradient(circle at 57% 18%, rgba(235, 244, 255, 0.74), transparent 26%),
        linear-gradient(100deg, #ffffff 0%, #ffffff 41%, #fafdff 64%, #ffffff 100%);
    box-shadow:
        0 16px 38px rgba(32, 79, 150, 0.1),
        inset 0 1px 0 #fff;
    transition: transform 0.35s var(--news-ease), box-shadow 0.35s ease, border-color 0.25s ease;
}

.featured-card:hover {
    border-color: rgba(65, 123, 237, 0.48);
    box-shadow: 0 23px 52px rgba(34, 82, 154, 0.15);
    transform: translateY(-3px);
}

.featured-card__copy {
    position: relative;
    z-index: 4;
    display: flex;
    width: 80%;
    min-height: 230px;
    padding: 52px 36px 40px 43px;
    flex-direction: column;
    align-items: flex-start;
}

.featured-card__title-row {
    display: flex;
    width: 100%;
    min-width: 0;
    align-items: center;
    gap: 34px;
}

.featured-card__badge {
    flex: 0 0 auto;
    padding: 6px 15px;
    border-radius: 5px;
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    line-height: 1;
    background: linear-gradient(135deg, #ff9d16, #ff6900);
    box-shadow: 0 7px 16px rgba(255, 111, 0, 0.22);
}

.featured-card__title-row h3 {
    min-width: 0;
    margin: 0;
    overflow: hidden;
    color: #111722;
    font-size: clamp(23px, 1.8vw, 31px);
    font-weight: 700;
    line-height: 1.36;
    letter-spacing: -0.6px;
    text-overflow: ellipsis;
    white-space: nowrap;
    transition: color 0.25s ease;
}

.featured-card:hover .featured-card__title-row h3 {
    color: #0b5de0;
}

.featured-card__copy > p {
    max-width: 520px;
    margin: 20px 0 0;
    display: -webkit-box;
    overflow: hidden;
    color: #6b7c99;
    font-size: 14px;
    line-height: 1.8;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
}

.featured-card__detail {
    display: inline-flex;
    align-items: center;
    gap: 16px;
    margin-top: auto;
    padding: 0;
    border: 0;
    color: #095ff0;
    font: inherit;
    font-size: 18px;
    cursor: pointer;
    background: transparent;
}

.featured-card__detail svg {
    width: 25px;
    height: 25px;
    transition: transform 0.25s var(--news-ease);
}

.featured-card__detail:hover svg {
    transform: translateX(5px);
}

.featured-card__art {
    position: absolute;
    z-index: 2;
    top: -47px;
    left: 42%;
    width: 35%;
    height: 310px;
    overflow: visible;
    pointer-events: none;
    filter: saturate(1.04);
    transform-origin: center;
    transition: transform 0.5s var(--news-ease), filter 0.35s ease;
}

.featured-card:hover .featured-card__art {
    filter: saturate(1.11);
    transform: translateY(-2px) scale(1.015);
}

.featured-card__date {
    position: absolute;
    z-index: 4;
    top: 50%;
    right: 36px;
    display: flex;
    align-items: center;
    gap: 20px;
    transform: translateY(-50%);
}

.featured-card__date-main {
    display: flex;
    align-items: baseline;
    color: #111;
    font-size: clamp(34px, 3vw, 44px);
    font-weight: 300;
    line-height: 1;
    letter-spacing: -2px;
}

.featured-card__date-main i {
    margin: 0 8px;
    color: #636c7b;
    font-style: normal;
    font-size: 28px;
}

.featured-card__date-side {
    position: relative;
    display: flex;
    padding-left: 20px;
    color: #74819a;
    font-size: 14px;
    line-height: 1.75;
    flex-direction: column;
}

.featured-card__date-side::before {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 1px;
    content: "";
    background: #c9d2e0;
}

.news-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px 14px;
    margin-top: 23px;
}

.news-item {
    position: relative;
    display: flex;
    min-width: 0;
    min-height: 103px;
    padding: 16px 27px 16px 33px;
    align-items: center;
    border: 1px solid rgba(211, 222, 239, 0.9);
    border-radius: 12px;
    outline: none;
    cursor: pointer;
    background: rgba(255, 255, 255, 0.96);
    box-shadow: 0 7px 18px rgba(42, 77, 133, 0.045);
    transition: border-color 0.25s ease, box-shadow 0.35s ease, transform 0.35s var(--news-ease);
}

.news-item:hover,
.news-item:focus-visible {
    border-color: rgba(45, 112, 244, 0.66);
    box-shadow: 0 15px 30px rgba(33, 90, 182, 0.11);
    transform: translateY(-3px);
}

.news-item__icon {
    position: relative;
    display: grid;
    width: 70px;
    height: 70px;
    flex: 0 0 70px;
    place-items: center;
    border: 1px solid rgba(91, 139, 228, 0.17);
    border-radius: 50%;
    color: #0d63f4;
    background: linear-gradient(145deg, #f7faff, #e9f1ff);
    box-shadow:
        inset 0 0 0 6px rgba(255, 255, 255, 0.72),
        0 7px 17px rgba(50, 98, 180, 0.08);
    transition: transform 0.35s var(--news-ease), color 0.25s ease;
}

.news-item__icon::after {
    position: absolute;
    inset: -4px;
    border: 1px solid rgba(59, 117, 222, 0.1);
    border-radius: 50%;
    content: "";
}

.news-item__icon svg {
    width: 30px;
    height: 30px;
}

.news-item:hover .news-item__icon {
    color: #0054df;
    transform: rotate(-4deg) scale(1.04);
}

.news-item__content {
    min-width: 0;
    margin-left: 31px;
}

.news-item__content h3 {
    margin: 0;
    overflow: hidden;
    color: #121a2a;
    font-size: 19px;
    font-weight: 650;
    line-height: 1.45;
    text-overflow: ellipsis;
    white-space: nowrap;
    transition: color 0.25s ease;
}

.news-item:hover .news-item__content h3 {
    color: #0b5fe8;
}

.news-item__content time {
    display: block;
    margin-top: 9px;
    color: #7a8bad;
    font-size: 14px;
    line-height: 1;
}

.news-item__arrow {
    display: grid;
    width: 42px;
    height: 42px;
    margin-left: auto;
    flex: 0 0 42px;
    place-items: center;
    border: 1px solid rgba(62, 119, 238, 0.45);
    border-radius: 50%;
    color: #1165f4;
    background: #fff;
    transition: color 0.25s ease, background 0.25s ease, transform 0.3s var(--news-ease);
}

.news-item__arrow svg {
    width: 22px;
    height: 22px;
}

.news-item:hover .news-item__arrow {
    color: #fff;
    background: #1268f4;
    transform: translateX(3px);
}

.news-showcase__more {
    display: flex;
    width: 292px;
    height: 52px;
    margin: 23px auto 0;
    align-items: center;
    justify-content: center;
    gap: 18px;
    border: 1.5px solid #0e64f3;
    border-radius: 999px;
    color: #0b60e8;
    font-size: 18px;
    text-decoration: none;
    background: rgba(255, 255, 255, 0.78);
    box-shadow: 0 8px 20px rgba(27, 91, 190, 0.05);
    transition: color 0.25s ease, background 0.25s ease, box-shadow 0.3s ease, transform 0.3s var(--news-ease);
}

.news-showcase__more svg {
    width: 22px;
    height: 22px;
    transition: transform 0.25s var(--news-ease);
}

.news-showcase__more:hover {
    color: #fff;
    background: #0b64f4;
    box-shadow: 0 13px 28px rgba(11, 100, 244, 0.24);
    transform: translateY(-2px);
}

.news-showcase__more:hover svg {
    transform: translateX(4px);
}

.news-loading__featured,
.news-loading__item {
    position: relative;
    overflow: hidden;
    border: 1px solid rgba(212, 224, 242, 0.8);
    background: rgba(255, 255, 255, 0.8);
}

.news-loading__featured::after,
.news-loading__item::after {
    position: absolute;
    inset: 0;
    content: "";
    background: linear-gradient(100deg, transparent 20%, rgba(255, 255, 255, 0.82) 42%, transparent 64%);
    transform: translateX(-100%);
    animation: newsSkeleton 1.4s infinite;
}

.news-loading__featured {
    height: 230px;
    border-radius: 17px;
}

.news-loading__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px 14px;
    margin-top: 23px;
}

.news-loading__item {
    height: 103px;
    border-radius: 12px;
}

.news-empty {
    display: flex;
    min-height: 580px;
    align-items: center;
    justify-content: center;
    gap: 15px;
    color: #7b8da9;
    font-size: 16px;
    flex-direction: column;
}

.news-empty svg {
    width: 68px;
    height: 68px;
}

.news-showcase__switching {
    position: absolute;
    z-index: 20;
    inset: 0;
    cursor: progress;
    border-radius: 17px;
    background: rgba(247, 250, 255, 0.15);
    backdrop-filter: blur(0.8px);
}

.news-showcase__switching span {
    position: absolute;
    top: 0;
    left: 0;
    width: 34%;
    height: 3px;
    border-radius: 999px;
    background: linear-gradient(90deg, transparent, #1269f6 40%, #9bc0ff 70%, transparent);
    animation: newsSwitch 1.05s ease-in-out infinite;
}

.news-fade-enter-active {
    transition: opacity 0.38s ease, transform 0.52s var(--news-ease), filter 0.38s ease;
}

.news-fade-leave-active {
    transition: opacity 0.16s ease, transform 0.18s ease;
}

.news-fade-enter-from {
    opacity: 0;
    filter: blur(2px);
    transform: translateY(12px);
}

.news-fade-leave-to,
.switch-fade-enter-from,
.switch-fade-leave-to {
    opacity: 0;
}

.switch-fade-enter-active,
.switch-fade-leave-active {
    transition: opacity 0.18s ease;
}

@keyframes newsSkeleton {
    to {
        transform: translateX(100%);
    }
}

@keyframes newsSwitch {
    from {
        transform: translateX(-110%);
    }
    to {
        transform: translateX(300%);
    }
}

@media (max-width: 1320px) {
    .news-showcase__header {
        padding-right: 50px;
        padding-left: 50px;
    }

    .news-showcase__body {
        margin-right: 50px;
        margin-left: 50px;
    }

    .featured-card__copy {
        width: 49%;
    }

    .featured-card__art {
        left: 43%;
        width: 42%;
    }

    .featured-card__date {
        right: 25px;
    }

    .news-item {
        padding-right: 20px;
        padding-left: 24px;
    }

    .news-item__content {
        margin-left: 22px;
    }
}

@media (max-width: 1080px) {
    .news-showcase__shell {
        min-height: 850px;
    }

    .news-showcase__header {
        min-height: 178px;
    }

    .news-showcase__tabs {
        width: 196px;
        min-width: 160px;
    }

    .featured-card__copy {
        width: 56%;
        padding-right: 20px;
    }

    .featured-card__title-row {
        gap: 20px;
    }

    .featured-card__art {
        left: 49%;
        width: 40%;
        opacity: 0.93;
    }

    .featured-card__date {
        top: 28px;
        right: 24px;
        transform: none;
    }

    .featured-card__date-main {
        font-size: 30px;
    }

    .featured-card__date-main i {
        font-size: 20px;
    }

    .news-item__icon {
        width: 60px;
        height: 60px;
        flex-basis: 60px;
    }

    .news-item__content {
        margin-left: 17px;
    }

    .news-item__content h3 {
        font-size: 16px;
    }

    .news-item__arrow {
        width: 38px;
        height: 38px;
        flex-basis: 38px;
    }
}

@media (max-width: 820px) {
    .news-showcase {
        padding-top: 12px;
    }

    .news-showcase__shell {
        min-height: auto;
        border-radius: 20px;
        background:
            radial-gradient(circle at 48% 4%, rgba(106, 172, 255, 0.45), transparent 24%),
            linear-gradient(180deg, #075ce9 0%, #2a7af3 18%, #dcecff 33%, #ffffff 49%, #ffffff 100%);
    }

    .news-showcase__header {
        min-height: auto;
        padding: 28px 24px 26px;
        align-items: flex-start;
        flex-direction: column;
        gap: 24px;
    }

    .news-showcase__eyebrow {
        font-size: 18px;
    }

    .news-showcase__heading h2 {
        font-size: 36px;
    }

    .news-showcase__heading p {
        margin-top: 8px;
        font-size: 14px;
    }

    .news-showcase__tabs {
        width: 100%;
        min-width: 0;
        height: 52px;
    }

    .news-showcase__tab {
        font-size: 16px;
    }

    .news-showcase__body {
        min-height: 500px;
        margin: 0;
    }

    .featured-card {
        min-height: 260px;
    }

    .featured-card__copy {
        width: 100%;
        min-height: 260px;
        padding: 35px 24px 28px;
    }

    .featured-card__title-row {
        padding-right: 90px;
        align-items: flex-start;
        gap: 14px;
    }

    .featured-card__badge {
        padding: 7px 12px;
        border-radius: 7px;
        font-size: 14px;
    }

    .featured-card__title-row h3 {
        display: -webkit-box;
        overflow: hidden;
        font-size: 21px;
        white-space: normal;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
    }

    .featured-card__copy > p {
        max-width: 74%;
        margin-top: 18px;
    }

    .featured-card__art {
        top: 50px;
        right: -100px;
        left: auto;
        width: 430px;
        height: 235px;
        opacity: 0.44;
    }

    .featured-card__date {
        top: 23px;
        right: 18px;
        gap: 8px;
    }

    .featured-card__date-main {
        font-size: 23px;
        letter-spacing: -1px;
    }

    .featured-card__date-main i {
        margin: 0 4px;
        font-size: 15px;
    }

    .featured-card__date-side {
        display: none;
    }

    .news-list,
    .news-loading__grid {
        grid-template-columns: 1fr;
    }

    .news-item {
        min-height: 92px;
        padding: 14px 18px;
    }

    .news-showcase__more {
        width: min(292px, 100%);
    }
}

@media (max-width: 520px) {
    .news-showcase__shell {
        border-radius: 16px;
    }

    .news-showcase__header {
        padding: 23px 18px 20px;
    }

    .news-showcase__heading h2 {
        font-size: 31px;
    }

    .news-showcase__heading p {
        line-height: 1.6;
    }

    .featured-card__copy {
        padding: 31px 18px 24px;
    }

    .featured-card__title-row {
        padding-right: 0;
        flex-direction: column;
        gap: 10px;
    }

    .featured-card__title-row h3 {
        padding-right: 50px;
        font-size: 19px;
    }

    .featured-card__copy > p {
        max-width: 100%;
        padding-right: 45px;
        font-size: 13px;
    }

    .featured-card__detail {
        font-size: 15px;
    }

    .featured-card__date {
        top: 35px;
        right: 16px;
    }

    .featured-card__date-main {
        font-size: 18px;
    }

    .featured-card__art {
        bottom: -10px;
        top: initial;
        right: 0;
        width: 200px;
        height: 150px;
        opacity: 0.32;
    }

    .news-item__icon {
        width: 52px;
        height: 52px;
        flex-basis: 52px;
    }

    .news-item__icon svg {
        width: 24px;
        height: 24px;
    }

    .news-item__content {
        margin-left: 14px;
    }

    .news-item__content h3 {
        font-size: 14px;
    }

    .news-item__content time {
        margin-top: 7px;
        font-size: 12px;
    }

    .news-item__arrow {
        width: 34px;
        height: 34px;
        flex-basis: 34px;
    }
}

@media (prefers-reduced-motion: reduce) {
    .news-showcase *,
    .news-showcase *::before,
    .news-showcase *::after {
        scroll-behavior: auto !important;
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
    }
}
</style>
